import React, { FC, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';
import {
  AdditionalInfo,
  CheckActiveCont,
  DropBodyTileCont,
  DropDownBody,
  IconImg,
  Label,
  SearchDropCont,
  SaveButton,
  Description,
} from './SearchDropdown.styles';
import NoData from '../NoData/NoData';
import SearchInputHeader from './SearchInputHeader/SearchInputHeader';
import SearchStaticHeader from './SearchStaticHeader/SearchStaticHeader';
import CheckIcon from '@/assets/icons/checkMark.svg?react';

interface ISearchDropdown {
  items: any[];
  placeholder: string;
  selectEl?: (el: any) => void;
  selectElements?: (el: any[]) => void;
  doSearch?: (phrase: string) => void;
  doFilter?: (phrase: string) => void;
  searchMode?: boolean;
  error?: string;
  label?: string;
  activeEl?: any;
  infoMessage?: string;
  width?: string;
  selectFullObject?: boolean;
  shortFlags?: boolean;
  sortMethod?: (value: string) => void;
  sortingState?: string | null;
  clearAction?: () => void;
  highlighted?: boolean;
  customInputRef?: any;
  displayedBodyLimit?: number;
  additionalBodyInfoKey?: string;
  noResultsMessage?: string;
  isDisabled?: boolean;
  isMulti?: boolean;
  onScrollToBottom?: () => void;
}

const SearchDropdown: FC<ISearchDropdown> = ({
  items,
  doSearch,
  error,
  searchMode,
  activeEl,
  placeholder,
  selectEl,
  selectElements,
  label,
  infoMessage,
  doFilter,
  width,
  selectFullObject,
  shortFlags,
  sortMethod,
  clearAction,
  highlighted,
  sortingState,
  customInputRef,
  displayedBodyLimit,
  additionalBodyInfoKey,
  noResultsMessage,
  isMulti,
  isDisabled,
  onScrollToBottom,
}) => {
  const { t } = useTranslation();
  const [dropSpace, setDropSpace] = useState<'small' | 'big'>('big');
  const [active, setActive] = useState(false);
  const [multiElements, setMultiElements] = useState<any>([]);
  const dropRef = useRef<HTMLDivElement | null>(null);
  const dropBodyRef = useRef<HTMLDivElement | null>(null);
  const localInputRef: any = useRef({ value: '' });
  const inputRef: any = customInputRef || localInputRef;

  const detectClickOutside = (e: MouseEvent) => {
    const { current }: MutableRefObject<any> = dropRef;

    const el = e?.target as HTMLElement;
    const isClickOnItem = el?.getAttribute('data-testid') === 'dropElement';

    if (current && !current.contains(e.target as Node) && !(isMulti && isClickOnItem)) {
      setActive(false);
    }

    if (isMulti && !isClickOnItem && selectElements && multiElements.length) {
      setMultiElements([]);
    }
  };

  const makeSelection = (name: string, fullObject?: any) => {
    if (!isMulti && selectEl) {
      if (fullObject && selectFullObject) {
        selectEl(fullObject);
      } else {
        selectEl(name);
      }
    } else {
      setMultiElements(
        multiElements.find((el: any) => el.id === fullObject.id)
          ? multiElements.filter((el: any) => el.id !== fullObject.id)
          : [...multiElements, fullObject]
      );
    }

    if (searchMode) {
      (inputRef.current as { value: string }).value = selectFullObject ? '' : name;
    }

    if (!isMulti) {
      setActive(false);
    }
  };

  const handleSave = () => {
    if (selectElements) {
      selectElements(multiElements);
    }
    setMultiElements([]);
    setActive(false);
  };

  useEffect(() => {
    if (!active || !dropRef.current) return;

    const rect = dropRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;

    const spaceAbove = rect.top;

    const dropdownHeight = 280;

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      setDropSpace('small');
    } else {
      setDropSpace('big');
    }
  }, [active]);

  const renderDropList = useMemo(() => {
    return (
      items.length > 0 &&
      [...items, ...(isMulti ? [{ isSaveButton: true }] : [])].map(
        (
          { shortDisplayName, description, name, icon, id, isSaveButton, ...rest }: any,
          i: number
        ) => {
          const limit = displayedBodyLimit || 1000;

          if (isSaveButton) {
            return (
              <SaveButton onClick={handleSave} disabled={!multiElements.length}>
                {t`Save`}
              </SaveButton>
            );
          }

          return (
            i < limit && (
              <DropBodyTileCont
                data-testid="dropElement"
                key={`dropElement${id}`}
                active={activeEl?.id === id}
                withDescription={!!description}
                onClick={() => makeSelection(name, { name, icon, id, shortDisplayName, ...rest })}
              >
                {icon && <IconImg src={icon} />}
                {shortFlags ? shortDisplayName : name}
                {(isMulti
                  ? multiElements.find((el: any) => el.id === id)
                  : activeEl?.id === id) && (
                  <CheckActiveCont>
                    <CheckIcon />
                  </CheckActiveCont>
                )}
                {additionalBodyInfoKey && (
                  <AdditionalInfo>{rest[additionalBodyInfoKey]}</AdditionalInfo>
                )}
                {description && <Description>{description}</Description>}
              </DropBodyTileCont>
            )
          );
        }
      )
    );
  }, [items, activeEl, multiElements, isMulti]);

  useEffect(() => {
    document.addEventListener('click', detectClickOutside);
    return () => {
      document.removeEventListener('click', detectClickOutside);
    };
  }, [multiElements]);

  useEffect(() => {
    const dropBody = dropBodyRef.current;
    if (!dropBody || !onScrollToBottom || !active) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = dropBody;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        onScrollToBottom();
      }
    };

    dropBody.addEventListener('scroll', handleScroll);
    return () => dropBody.removeEventListener('scroll', handleScroll);
  }, [onScrollToBottom, active]);

  const searchForList = debounce(({ target }: any) => {
    if (!active) {
      setActive(true);
    }
    const { value } = target;
    if (value && doSearch) {
      return doSearch(value);
    }
    const phrase: any = value || '';
    return doSearch && doSearch(phrase);
  }, 250);

  const filterList = ({ target }: any) => {
    if (!active) {
      setActive(true);
    }
    const { value } = target;
    if (doFilter && selectEl) {
      doFilter(value);
      selectEl(value);
    }
  };

  return (
    <>
      {label && <Label data-testid="searchDropLabel">{label}</Label>}
      <SearchDropCont width={width} ref={dropRef}>
        {searchMode ? (
          <SearchInputHeader
            width={width}
            data-testid="SearchDrophead"
            active={active}
            placeholder={placeholder}
            searchForList={doFilter ? filterList : searchForList}
            infoMessage={infoMessage}
            inputRef={inputRef}
            setActive={!isDisabled ? setActive : () => null}
            error={error}
          />
        ) : (
          <SearchStaticHeader
            data-testid="StaticDrophead"
            sortMethod={sortMethod}
            active={active}
            placeholder={placeholder}
            infoMessage={infoMessage}
            setActive={!isDisabled ? setActive : () => null}
            error={error}
            activeEl={activeEl}
            width={width}
            shortFlags={shortFlags}
            clearAction={clearAction}
            highlighted={highlighted && !active}
            sortingState={sortingState}
            isDisabled={isDisabled}
          />
        )}
        {active && (
          <DropDownBody
            ref={dropBodyRef}
            data-testid="DropBody"
            error={!!error}
            width={width}
            isMulti={isMulti}
            direction={dropSpace}
          >
            {items.length > 0 ? (
              renderDropList
            ) : (
              <NoData message={noResultsMessage || t('noSearchData')} height="100px" />
            )}
          </DropDownBody>
        )}
      </SearchDropCont>
    </>
  );
};

export default SearchDropdown;
